import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  X, ArrowRight, CheckCircle, Users, BookOpen, 
  Compass, ChartLineUp, Crown, Sparkle, HandHeart
} from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const WelcomeModal = ({ isOpen, onClose }) => {
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);

  const steps = [
    {
      id: 'welcome',
      title: `Welcome to the Brotherhood, ${user?.name?.split(' ')[0] || 'King'}`,
      description: "You've just taken the first step toward building legacy wealth with purpose. This community is built by Black men, for Black men. Let's get you oriented.",
      icon: HandHeart,
      highlight: 'FREE FOREVER',
      highlightText: "Your membership is free. No credit card. No trial. Just brotherhood."
    },
    {
      id: 'community',
      title: 'Connect With Your Brothers',
      description: "The Feed is where the brotherhood comes alive. Share wins, ask questions, hold each other accountable, and grow together.",
      icon: Users,
      features: [
        { label: 'Feed', desc: 'Daily discussions' },
        { label: 'Wins', desc: 'Celebrate victories' },
        { label: 'Accountability', desc: 'Stay on track' },
        { label: 'Faith & Business', desc: 'Build with purpose' }
      ]
    },
    {
      id: 'resources',
      title: 'Learn & Grow',
      description: "Access free training, daily devotionals, and curated resources designed to help you build wealth with wisdom.",
      icon: BookOpen,
      features: [
        { label: 'Daily Devotionals', desc: '31-day spiritual journey' },
        { label: 'Training Courses', desc: 'Wealth-building education' },
        { label: 'Links & Resources', desc: 'Curated tools' },
        { label: 'Video Library', desc: 'On-demand content' }
      ]
    },
    {
      id: 'tools',
      title: 'Investment Tools',
      description: "Practice building wealth before risking real money. Track your commitment and learn by doing.",
      icon: ChartLineUp,
      features: [
        { label: '$5 Movement', desc: 'Weekly commitment tracker' },
        { label: 'Paper Portfolio', desc: 'Simulated investing' },
        { label: 'Wealth Intelligence', desc: 'AI-powered education' }
      ]
    },
    {
      id: 'next',
      title: "You're Ready",
      description: "The brotherhood is here to support you. Start by introducing yourself in the Feed, or dive into today's devotional.",
      icon: Sparkle,
      actions: [
        { label: 'Go to Community', route: '/community', primary: true },
        { label: "Today's Devotional", route: '/devotionals', primary: false }
      ]
    }
  ];

  const currentStepData = steps[currentStep];

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleComplete = async (route = '/community') => {
    setLoading(true);
    try {
      await axios.post(`${API}/api/onboarding/complete`, {}, {
        headers: getAuthHeader()
      });
      onClose();
      navigate(route);
    } catch (error) {
      // Still close and navigate even if API fails
      onClose();
      navigate(route);
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = async () => {
    await handleComplete('/community');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.9)' }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 25% 25%, #C4A661 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }} />
        </div>

        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', damping: 25 }}
          className="relative w-full max-w-2xl bg-[#111] border border-[#333] rounded-2xl overflow-hidden"
        >
          {/* Skip Button */}
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 z-10 text-white/50 hover:text-white transition-colors"
          >
            <X size={24} />
          </button>

          {/* Progress Bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-[#222]">
            <motion.div
              className="h-full bg-[#C4A661]"
              initial={{ width: 0 }}
              animate={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>

          {/* Content */}
          <div className="p-8 pt-12">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {/* Icon */}
                <div className="mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-[#C4A661]/20 flex items-center justify-center">
                    <currentStepData.icon size={32} className="text-[#C4A661]" weight="fill" />
                  </div>
                </div>

                {/* Title */}
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  {currentStepData.title}
                </h2>

                {/* Highlight Badge */}
                {currentStepData.highlight && (
                  <div className="mb-4">
                    <span className="inline-block px-3 py-1 bg-[#10B981]/20 text-[#10B981] text-sm font-bold rounded-full">
                      {currentStepData.highlight}
                    </span>
                    <p className="text-[#10B981]/80 text-sm mt-2">{currentStepData.highlightText}</p>
                  </div>
                )}

                {/* Description */}
                <p className="text-white/70 text-lg leading-relaxed mb-8">
                  {currentStepData.description}
                </p>

                {/* Features Grid */}
                {currentStepData.features && (
                  <div className="grid grid-cols-2 gap-3 mb-8">
                    {currentStepData.features.map((feature, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-3 p-3 bg-[#1a1a1a] rounded-lg border border-[#333]"
                      >
                        <CheckCircle size={20} className="text-[#C4A661] mt-0.5 flex-shrink-0" weight="fill" />
                        <div>
                          <p className="text-white font-medium text-sm">{feature.label}</p>
                          <p className="text-white/50 text-xs">{feature.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Final Step Actions */}
                {currentStepData.actions && (
                  <div className="flex flex-col sm:flex-row gap-3 mb-4">
                    {currentStepData.actions.map((action, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleComplete(action.route)}
                        disabled={loading}
                        className={`flex-1 py-4 px-6 rounded-xl font-bold transition-all flex items-center justify-center gap-2 ${
                          action.primary
                            ? 'bg-[#C4A661] hover:bg-[#D4B671] text-black'
                            : 'bg-[#222] hover:bg-[#333] text-white border border-[#444]'
                        }`}
                      >
                        {action.label}
                        <ArrowRight size={18} weight="bold" />
                      </button>
                    ))}
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation */}
          <div className="px-8 pb-8 flex items-center justify-between">
            {/* Step Indicator */}
            <div className="flex gap-2">
              {steps.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentStep(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentStep
                      ? 'bg-[#C4A661] w-6'
                      : idx < currentStep
                      ? 'bg-[#C4A661]/50'
                      : 'bg-[#333]'
                  }`}
                />
              ))}
            </div>

            {/* Navigation Buttons */}
            {currentStep < steps.length - 1 && (
              <div className="flex gap-3">
                {currentStep > 0 && (
                  <button
                    onClick={handlePrev}
                    className="px-6 py-2 text-white/70 hover:text-white transition-colors"
                  >
                    Back
                  </button>
                )}
                <button
                  onClick={handleNext}
                  className="px-6 py-2 bg-[#C4A661] hover:bg-[#D4B671] text-black font-bold rounded-lg transition-all flex items-center gap-2"
                >
                  Next
                  <ArrowRight size={16} weight="bold" />
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default WelcomeModal;
